import { deepMerge } from 'js/utils/Utils'
import { WebGLRenderer, Scene } from 'three'

import BasePerspectiveCameraController from 'js/webgl/base/camera/BasePerspectiveCameraController'
import Common from 'js/_core/Common'
import { get100lvh } from 'js/utils/Utils'

const defaults = {
  instanceName: 'BaseStage',
  dpr: Math.min(window.devicePixelRatio, 2),
  rendererColor: {
    color: 0x000000,
    alpha: 1,
  },
  webGLRendererParameters: {
    antialias: false,
    alpha: false,
  },
  sceneParameters: {
    autoUpdate: false,
  },
}

export default class BaseStage {
  constructor(options = {}) {
    this.webgl = {}
    this.detection = null

    this.$canvas = null
    this.$canvasContainer = null

    this.renderer = null
    this.scene = null
    this.camera = null

    this.stageSize = {
      width: 0,
      height: 0,
      widthRatio: 0,
      heightRatio: 0,
      aspect: { xy: 0, yx: 0 },
    }

    this.gui = null

    deepMerge(this, defaults, options)

    if (Common.detection.isMobileLayout) {
      this.dpr = Math.min(window.devicePixelRatio, 2)
    }
  }

  init() {
    if (this.$canvas) {
      this.webGLRendererParameters.canvas = this.$canvas
    } else {
      this.webGLRendererParameters.canvas = document.createElement('canvas')
    }

    this.renderer = new WebGLRenderer(this.webGLRendererParameters)
    this.scene = new Scene()

    this._setStageSize()
    this._setCameraInstance()
    this._initRenderer()
  }

  destroy() {
    if (this.camera) {
      this.camera.destroy()
    }

    if (this.renderer) {
      this.renderer.dispose()
      this.renderer.forceContextLoss()
    }

    this.camera = null
    this.renderer = null
    this.scene = null
  }

  _setCameraInstance() {
    this.camera = new BasePerspectiveCameraController({
      stage: this,
      webgl: this.webgl,
      sizes: this.stageSize,
      renderer: this.renderer,
      detection: this.detection,
      gui: this.gui,
    })

    this.camera.init()
  }

  _initRenderer() {
    this.renderer.setClearColor(this.rendererColor.color, this.rendererColor.alpha)
    this.setRenderer()
  }

  setRenderer() {
    if (this.renderer) {
      this.renderer.setSize(this.stageSize.width, this.stageSize.height)
      this.renderer.setPixelRatio(this.dpr)
    }
  }

  hideRenderer() {
    if (this.renderer) {
      this.renderer.setSize(0, 0)
      this.renderer.setPixelRatio(0.1)
    }
  }

  _setStageSize() {
    if (this.$canvasContainer) {
      const rect = this.$canvasContainer.getBoundingClientRect()
      this.stageSize.width = rect.width
      this.stageSize.height = rect.height
    } else {
      this.stageSize.width = document.body.clientWidth
      this.stageSize.height = window.innerHeight

      if (Common.detection.isMobileDevice || Common.detection.isTabletDevice) {
        // モバイルデバイスでは、画面の高さを基準にする
        this.stageSize.height = get100lvh()
      }
    }

    this.stageSize.widthRatio = 1 / this.stageSize.width
    this.stageSize.heightRatio = 1 / this.stageSize.height
    this.stageSize.aspect.xy = this.stageSize.width / this.stageSize.height
    this.stageSize.aspect.yx = this.stageSize.height / this.stageSize.width
    this.setRenderer()
    this.onRender()
  }

  onRender() {
    this.renderer.render(this.scene, this.camera.instance)
  }

  onResize() {
    this._setStageSize()

    if (this.camera) {
      this.camera.onResize(this.stageSize)
    }

    requestAnimationFrame(() => {
      this.setRenderer()
    })
  }

  onRaf(timeStamp) {
    if (this.camera) {
      this.camera.onRaf(timeStamp)
    }
    this.onRender()
  }

  // helper
  _checkWebGLRenderInfo() {
    // if (this.renderer) {
    //   console.log(this.renderer.info)
    // }
  }
}